import React from 'react';
import {
  Dialog,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Paper,
  Avatar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Autocomplete,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, DeleteOutline, Add as AddIcon, CheckCircle, Cancel as CancelIcon } from '@mui/icons-material';
import { PictureAsPdf, KeyboardArrowRight, PhoneIphone } from '@mui/icons-material';
import contractorService, { Contractor } from '../../services/contractorService';
import { getAllProjects, Project, getInitials } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { listUsersPublicForBusinessForms } from '../../services/userService';
import { createTransferInvoice } from '../../services/invoicesService';
import { useNotification } from '../layouts/Layout';
import api from '../../services/api';
import { normalizeUnit, ensureUnitOptions } from '../../utils/units';
import { getSocket } from '../../services/realtimeService';

export interface TransferInvoiceFormData {
  invoiceDate: string;
  receivedDate: string;
  projectId: string;
  invoiceNumber: string;
  contractorId?: string;
  contractorName: string;
  netAmount: number | '';
  vatRate: 0 | 8 | 23 | 'zw' | 'różne';
  grossAmount: number | '';
  dueDate: string;
  priority: 'zwykły' | 'pilny';
  paymentMethod: 'Przelew' | 'Proforma' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)';
  documentKind: 'vat' | 'advance' | 'proforma' | 'correction';
  file?: File | null;
  legalAcceptance: boolean;
  calcMode?: 'net' | 'gross' | 'none';
  bankAccount?: string;
  nip?: string;
  contractorAddress?: string;
  contractorPostalCity?: string;
  warehouse?: string;
  ksefNumber?: string;
  /** Osoba, która wprowadziła fakturę (kolumna „W" w tabeli faktur). */
  enteredByUserId?: string;
}

interface InvoiceLineItem {
  id: number;
  name: string;
  quantity: number | '';
  unit: string;
  unitNetPrice: number | '';
  unitGrossPrice: number | '';
  vatRate: number | string;
  netValue: number | '';
  grossValue: number | '';
}

interface AddTransferInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: TransferInvoiceFormData) => void;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const AddTransferInvoiceDialog: React.FC<AddTransferInvoiceDialogProps> = ({ open, onClose, onSave }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [projects, setProjects] = React.useState<Array<{ project_id: string; project_name: string }>>([]);
  const STATIC_PROJECTS: Array<{ project_id: string; project_name: string }> = [
    { project_id: 'S14', project_name: 'SERWIS GWARANCYJNY: USŁUGI' },
    { project_id: 'S13', project_name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
    { project_id: 'S12', project_name: 'LEASINGI' },
    { project_id: 'S11', project_name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
    { project_id: 'S10', project_name: 'USŁUGI TELEINFORMATYCZNE' },
    { project_id: 'S9',  project_name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
    { project_id: 'S8',  project_name: 'NAJEM POWIERZCHNI BIUROWEJ' },
    { project_id: 'S7',  project_name: 'PRACOWNICY B2B' },
    { project_id: 'S6',  project_name: 'ZARZĄD' },
    { project_id: 'S5',  project_name: 'USŁUGI WŁASNE' },
    { project_id: 'S4',  project_name: 'MATERIAŁY WŁASNE' },
    { project_id: 'S3',  project_name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
    { project_id: 'S2',  project_name: 'WYPOSAŻENIE - do 3.000 zł' },
    { project_id: 'S1',  project_name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
    { project_id: 'R11', project_name: 'PODATEK CIT' },
    { project_id: 'R10', project_name: 'PODATEK VAT' },
    { project_id: 'R9',  project_name: 'ZUS' },
    { project_id: 'R8',  project_name: 'PODATKI - PIT' },
    { project_id: 'R7',  project_name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
    { project_id: 'R6',  project_name: 'OBSŁUGA BANKOWA' },
    { project_id: 'R5',  project_name: 'UBEZPIECZENIA I POLISY' },
    { project_id: 'R4',  project_name: 'PRACOWNICY UoP, UD, UZ' },
    { project_id: 'R3',  project_name: 'ZABEZPIECZENIE KONTRAKTÓW' },
    { project_id: 'R2',  project_name: 'KOSZTY KREDYTÓW' },
    { project_id: 'R1',  project_name: 'AMORTYZACJA' },
    { project_id: '.UN', project_name: 'USŁUGI NOCLEGOWE' },
    { project_id: '.UG', project_name: 'USŁUGI GASTRONOMICZNE' },
    { project_id: '.P2', project_name: 'POJAZDY - 50% ODLICZENIA VAT' },
    { project_id: '.P1', project_name: 'POJAZDY - 100% ODLICZENIA VAT' },
  ];
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  const [contractorSearchQuery, setContractorSearchQuery] = React.useState('');
  const [contractorOptions, setContractorOptions] = React.useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = React.useState(false);

  // Lista użytkowników do dropdownu „Wprowadził" (kolumna „W"); ładowana raz po otwarciu.
  const [enteredByUsers, setEnteredByUsers] = React.useState<Array<{ user_id: string; display_name?: string; email: string; avatar_url?: string }>>([]);

  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [showErrors, setShowErrors] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [form, setForm] = React.useState<TransferInvoiceFormData>({
    invoiceDate: '',
    receivedDate: '',
    projectId: '',
    invoiceNumber: '',
    contractorId: undefined,
    contractorName: '',
    netAmount: '',
    vatRate: 23,
    grossAmount: '',
    dueDate: '',
    priority: 'zwykły',
    paymentMethod: 'Przelew',
    documentKind: 'vat',
    file: null,
    legalAcceptance: false,
    calcMode: 'net',
    bankAccount: '',
    nip: '',
    contractorAddress: '',
    contractorPostalCity: '',
    warehouse: '',
    ksefNumber: '',
    // Domyślnie aktualnie zalogowany user; można zmienić przed zapisem.
    enteredByUserId: (user as any)?.user_id || '',
  });
  const [formTab, setFormTab] = React.useState(0); // 0 = Informacje, 1 = Wartości i pozycje
  const [withLineItems, setWithLineItems] = React.useState(false); // false = bez pozycji, true = z pozycjami
  const [selectedContractorNip, setSelectedContractorNip] = React.useState<string>('');
  const [bankAccountCheckStatus, setBankAccountCheckStatus] = React.useState<'idle' | 'checking' | 'valid' | 'invalid' | 'error'>('idle');
  const [bankAccountCheckedAt, setBankAccountCheckedAt] = React.useState<string | null>(null);
  const emptyLineItem = (): InvoiceLineItem => ({ id: Date.now(), name: '', quantity: '', unit: 'szt.', unitNetPrice: '', unitGrossPrice: '', vatRate: 23, netValue: '', grossValue: '' });
  const [lineItems, setLineItems] = React.useState<InvoiceLineItem[]>([
    { id: 1, name: '', quantity: '', unit: 'szt.', unitNetPrice: '', unitGrossPrice: '', vatRate: 23, netValue: '', grossValue: '' }
  ]);
  // Flaga: użytkownik edytuje pozycje (a nie tylko wczytujemy je programowo).
  // Steruje efektem sumującym poniżej – nie nadpisuje kwoty nagłówka po samej
  // inicjalizacji.
  const userEditedItemsRef = React.useRef(false);
  const [focusedPriceField, setFocusedPriceField] = React.useState<string | null>(null);
  const fmtPrice = (val: any, fieldKey?: string) => {
    if (fieldKey && focusedPriceField === fieldKey) return val;
    if (typeof val === 'number') return val.toFixed(2);
    return (val === '' || val === undefined || val === null) ? '' : val;
  };
  const addLineItem = () => {
    userEditedItemsRef.current = true;
    setLineItems(prev => [...prev, emptyLineItem()]);
  };
  const removeLineItem = (id: number) => {
    userEditedItemsRef.current = true;
    setLineItems(prev => prev.length > 1 ? prev.filter(i => i.id !== id) : prev);
  };
  const updateLineItem = (id: number, field: keyof InvoiceLineItem, value: any) => {
    userEditedItemsRef.current = true;
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      const qty = typeof updated.quantity === 'number' ? updated.quantity : 0;
      const rate = typeof updated.vatRate === 'number' ? updated.vatRate : 0;
      if (form.calcMode === 'gross') {
        const gPrice = typeof updated.unitGrossPrice === 'number' ? updated.unitGrossPrice : 0;
        if (qty > 0 && gPrice > 0) {
          updated.grossValue = round2(qty * gPrice);
          updated.netValue = rate > 0 ? round2(updated.grossValue / (1 + rate / 100)) : updated.grossValue;
          updated.unitNetPrice = rate > 0 ? round2(gPrice / (1 + rate / 100)) : gPrice;
        } else { updated.netValue = ''; updated.grossValue = ''; }
      } else {
        const nPrice = typeof updated.unitNetPrice === 'number' ? updated.unitNetPrice : 0;
        if (qty > 0 && nPrice > 0) {
          updated.netValue = round2(qty * nPrice);
          updated.grossValue = round2(updated.netValue * (1 + rate / 100));
          updated.unitGrossPrice = round2(nPrice * (1 + rate / 100));
        } else { updated.netValue = ''; updated.grossValue = ''; }
      }
      return updated;
    }));
  };

  // Sumowanie wartości z pozycji do nagłówka. Odpala się TYLKO po faktycznej
  // edycji pozycji przez użytkownika; samo ustawienie pozycji programowo
  // (np. import) nie nadpisuje netAmount/grossAmount.
  React.useEffect(() => {
    if (!withLineItems) return;
    if (!userEditedItemsRef.current) return;
    let totalNet = 0;
    let totalGross = 0;
    const filledItems = lineItems.filter(item => typeof item.netValue === 'number' || typeof item.grossValue === 'number');
    lineItems.forEach(item => {
      if (typeof item.netValue === 'number') totalNet += item.netValue;
      if (typeof item.grossValue === 'number') totalGross += item.grossValue;
    });
    const vatRates = filledItems.map(item => item.vatRate);
    const uniqueRates = Array.from(new Set(vatRates));
    const resolvedVatRate = uniqueRates.length === 1 ? uniqueRates[0] : 'różne';
    setForm(p => ({
      ...p,
      netAmount: totalNet > 0 ? round2(totalNet) : '',
      grossAmount: totalGross > 0 ? round2(totalGross) : '',
      vatRate: resolvedVatRate as any,
    }));
  }, [lineItems, withLineItems]);

  // Style pól zgodne z AddProjectForm
  const textFieldSx = {
    '& .MuiOutlinedInput-root': { height: 32, backgroundColor: '#fff', borderRadius: '4px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': { padding: '8px 12px', boxSizing: 'border-box', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
    '& .MuiFormHelperText-root': { ml: 0 },
    '& .MuiInputLabel-root': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '12px', color: '#2D2D32' }
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': { height: 32, backgroundColor: '#fff', borderRadius: '4px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': { padding: '8px 12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  // Skrót klawiszowy "§" do przełączania zakładek
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === '§') {
        e.preventDefault();
        setFormTab((prev) => (prev === 0 ? 1 : 0));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  // Przelicz pozycje przy zmianie trybu obliczeń (netto/brutto)
  React.useEffect(() => {
    setLineItems(prev => prev.map(item => {
      const qty = typeof item.quantity === 'number' ? item.quantity : 0;
      const rate = typeof item.vatRate === 'number' ? item.vatRate : 0;
      if (form.calcMode === 'gross') {
        const nPrice = typeof item.unitNetPrice === 'number' ? item.unitNetPrice : 0;
        if (nPrice > 0) {
          const gPrice = round2(nPrice * (1 + rate / 100));
          return { ...item, unitGrossPrice: gPrice, grossValue: qty > 0 ? round2(qty * gPrice) : '', netValue: qty > 0 ? round2(qty * nPrice) : '' };
        }
      } else {
        const gPrice = typeof item.unitGrossPrice === 'number' ? item.unitGrossPrice : 0;
        if (gPrice > 0 && rate > 0) {
          const nPrice = round2(gPrice / (1 + rate / 100));
          return { ...item, unitNetPrice: nPrice, netValue: qty > 0 ? round2(qty * nPrice) : '', grossValue: qty > 0 ? round2(qty * gPrice) : '' };
        }
      }
      return item;
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.calcMode]);

  const [scanWaiting, setScanWaiting] = React.useState(false);
  const [scanSessionId, setScanSessionId] = React.useState<string | null>(null);
  const scanHandlerRef = React.useRef<((p: any) => void) | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        setLoadingProjects(true);
        const all = await getAllProjects();
        const active = all.filter((p) => p.project_phase === 'W REALIZACJI')
          .map((p) => ({ project_id: p.project_id, project_name: p.project_name }));
        setProjects([...active, ...STATIC_PROJECTS]);
      } finally {
        setLoadingProjects(false);
      }
    };
    load();
    // Rachunki bankowe Unitresco nie są już ładowane tutaj — pole rachunku dotyczy kontrahenta
  }, [open]);

  // Lista użytkowników do dropdownu „Wprowadził" (kolumna „W" w tabeli faktur).
  // Używamy publicznego endpointu z `forBusiness=1` (bez pracowników terenowych).
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listUsersPublicForBusinessForms()
      .then((list) => { if (!cancelled) setEnteredByUsers(list as any); })
      .catch(() => { if (!cancelled) setEnteredByUsers([]); });
    return () => { cancelled = true; };
  }, [open]);

  // Po asynchronicznym załadowaniu `user` (AuthContext) ustaw domyślnego
  // „wprowadzającego", o ile pole jeszcze puste. Bez tego kolumna „W"
  // zostawała pusta przy szybkim otwarciu dialogu.
  React.useEffect(() => {
    const uid = (user as any)?.user_id;
    if (uid && !form.enteredByUserId) {
      setForm((p) => ({ ...p, enteredByUserId: uid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Synchronizuj contractorSearchQuery z form.contractorName tylko przy otwieraniu dialogu
  React.useEffect(() => {
    if (!open) return;
    if (form.contractorName) {
      setContractorSearchQuery(form.contractorName);
    } else {
      setContractorSearchQuery('');
    }
  }, [open]);

  // Automatycznie pobierz NIP kontrahenta, gdy contractorId jest ustawiony
  React.useEffect(() => {
    if (!open || !form.contractorId || selectedContractorNip) return;
    contractorService.getContractorById(form.contractorId)
      .then((c) => {
        if (c?.nip) { setSelectedContractorNip(c.nip); setForm((p) => ({ ...p, nip: c.nip })); }
      })
      .catch(() => {});
  }, [open, form.contractorId, selectedContractorNip]);

  // Imperatywne przeliczanie netto/brutto/VAT. NIE używamy useEffect – każda
  // zmiana wynikowego pola odbywa się tylko w handlerach onChange poniżej.
  // Dzięki temu wartości wpisane ręcznie (lub zaimportowane) nigdy nie są
  // zaokrąglane „pod sukno" przez efekty renderowane na inicjalizacji.
  const recalcOnNetEdit = (
    prev: TransferInvoiceFormData,
    nextNet: number | '',
  ): Partial<TransferInvoiceFormData> => {
    const updates: any = { netAmount: nextNet };
    if (prev.vatRate === 'różne' || !prev.calcMode || prev.calcMode === 'gross') return updates;
    const vatPercent = prev.vatRate === 'zw' ? 0 : Number(prev.vatRate);
    if (typeof nextNet === 'number' && nextNet > 0) {
      updates.grossAmount = round2(nextNet * (1 + vatPercent / 100));
    } else if (nextNet === '') {
      updates.grossAmount = '';
    }
    return updates;
  };
  const recalcOnGrossEdit = (
    prev: TransferInvoiceFormData,
    nextGross: number | '',
  ): Partial<TransferInvoiceFormData> => {
    const updates: any = { grossAmount: nextGross };
    if (prev.vatRate === 'różne' || !prev.calcMode || prev.calcMode === 'net') return updates;
    const vatPercent = prev.vatRate === 'zw' ? 0 : Number(prev.vatRate);
    if (typeof nextGross === 'number' && nextGross > 0) {
      updates.netAmount = vatPercent === 0 ? nextGross : round2(nextGross / (1 + vatPercent / 100));
    } else if (nextGross === '') {
      updates.netAmount = '';
    }
    return updates;
  };
  const recalcOnVatRateEdit = (
    prev: TransferInvoiceFormData,
    nextRate: 0 | 8 | 23 | 'zw' | 'różne',
  ): Partial<TransferInvoiceFormData> => {
    const updates: any = {
      vatRate: nextRate,
      calcMode:
        nextRate === 'różne'
          ? 'none'
          : prev.calcMode === 'none' || !prev.calcMode
            ? 'net'
            : prev.calcMode,
    };
    if (nextRate === 'różne') return updates;
    const vatPercent = nextRate === 'zw' ? 0 : Number(nextRate);
    if (updates.calcMode === 'net' && typeof prev.netAmount === 'number' && prev.netAmount > 0) {
      updates.grossAmount = round2(prev.netAmount * (1 + vatPercent / 100));
    } else if (updates.calcMode === 'gross' && typeof prev.grossAmount === 'number' && prev.grossAmount > 0) {
      updates.netAmount = vatPercent === 0 ? prev.grossAmount : round2(prev.grossAmount / (1 + vatPercent / 100));
    }
    return updates;
  };

  const searchContractors = React.useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setContractorOptions([]);
      return;
    }
    try {
      setLoadingContractors(true);
      const response = await contractorService.filterContractors({ searchText: query }, 1, 10);
      setContractorOptions(response.contractors);
    } catch (e) {
      setContractorOptions([]);
    } finally {
      setLoadingContractors(false);
    }
  }, []);

  // Wyszukiwanie kontrahentów po NIP — wyniki trafiają do contractorOptions
  const nipSearchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchContractorsByNip = React.useCallback((nipValue: string) => {
    if (nipSearchTimer.current) clearTimeout(nipSearchTimer.current);
    const digits = nipValue.replace(/[^0-9]/g, '');
    if (digits.length < 2) return;
    nipSearchTimer.current = setTimeout(async () => {
      try {
        setLoadingContractors(true);
        const response = await contractorService.filterContractors({ nipText: digits }, 1, 10);
        setContractorOptions(response.contractors);
      } catch (e) {
        setContractorOptions([]);
      } finally {
        setLoadingContractors(false);
      }
    }, 300);
  }, []);

  const onDropFile = (f: File) => {
    if (f && f.type === 'application/pdf') {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
      const url = URL.createObjectURL(f);
      setFileUrl(url);
      setForm((prev) => ({ ...prev, file: f }));
    }
  };

  const canSave =
    !!form.invoiceDate &&
    !!form.receivedDate &&
    !!form.projectId &&
    !!form.contractorName &&
    (
      (form.vatRate === 'różne' && typeof form.netAmount === 'number' && form.netAmount > 0 && typeof form.grossAmount === 'number' && form.grossAmount > 0) ||
      (form.vatRate !== 'różne' &&
        (
          (form.calcMode === 'net' && typeof form.netAmount === 'number' && form.netAmount > 0) ||
          (form.calcMode === 'gross' && typeof form.grossAmount === 'number' && form.grossAmount > 0)
        )
      )
    ) &&
    !!form.dueDate &&
    !!form.file &&
    !!form.legalAcceptance;

  const resetState = () => {
    setForm({
      invoiceDate: '',
      receivedDate: '',
      projectId: '',
      invoiceNumber: '',
      contractorId: undefined,
      contractorName: '',
      netAmount: '',
      vatRate: 23,
      grossAmount: '',
      dueDate: '',
      priority: 'zwykły',
      paymentMethod: 'Przelew',
      documentKind: 'vat',
      file: null,
      legalAcceptance: false,
      calcMode: 'net',
    });
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setContractorSearchQuery('');
    setContractorOptions([]);
    setSelectedContractorNip('');
    setBankAccountCheckStatus('idle');
    setBankAccountCheckedAt(null);
    userEditedItemsRef.current = false;
    // cleanup scan listener
    try {
      const s = getSocket();
      const h = scanHandlerRef.current;
      if (h) s.off('scan:completed', h);
    } catch {}
    scanHandlerRef.current = null;
    setScanWaiting(false);
    setScanSessionId(null);
  };

  const handleClose = () => {
    resetState();
    // Jeśli otwarto jako osobną kartę, zamknij kartę
    if (window.opener) {
      window.close();
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    if (saving) return;
    if (!canSave || !form.file) { setShowErrors(true); return; }
    const payload = {
      document_type: form.paymentMethod,
      document_kind: form.documentKind,
      document_date: form.invoiceDate,
      received_date: form.receivedDate,
      project_id: form.projectId,
      contractor_id: form.contractorId || undefined,
      contractor_name: form.contractorName,
      account_number: form.bankAccount || undefined,
      nip: form.nip || undefined,
      contractor_address: form.contractorAddress || undefined,
      contractor_postal_city: form.contractorPostalCity || undefined,
      warehouse: form.warehouse || undefined,
      ksef_number: form.ksefNumber || undefined,
      white_list_status: bankAccountCheckStatus === 'valid' || bankAccountCheckStatus === 'invalid' ? bankAccountCheckStatus : undefined,
      white_list_checked_at: bankAccountCheckedAt || undefined,
      net_amount: typeof form.netAmount === 'number' ? form.netAmount : 0,
      vat_rate: (form.vatRate === 'zw' ? 'zw' : String(form.vatRate)) as any,
      gross_amount: typeof form.grossAmount === 'number' ? form.grossAmount : 0,
      calc_mode: form.calcMode || 'net',
      due_date: form.dueDate,
      invoice_number: form.invoiceNumber || undefined,
      priority: form.priority,
      comment: undefined,
      // Pole „Wprowadził" – jeśli pominięte, backend ustawi na `req.user.id`.
      created_by_user_id: form.enteredByUserId || undefined,
      pdf: form.file,
      items: lineItems.filter(it => it.name || (typeof it.quantity === 'number' && it.quantity > 0) || (typeof it.unitNetPrice === 'number' && it.unitNetPrice > 0)).map(it => ({
        name: it.name,
        quantity: it.quantity,
        unit: it.unit,
        unitNetPrice: it.unitNetPrice,
        unitGrossPrice: it.unitGrossPrice,
        vatRate: it.vatRate,
        netValue: it.netValue,
        grossValue: it.grossValue,
      })),
    } as any;
    showNotification('Trwa przesyłanie PDF do Google Drive i zapisywanie w bazie...', 'info');
    setSaving(true);
    createTransferInvoice(payload, (user as any)?.google_access_token)
      .then(() => {
        showNotification('Faktura została zapisana i wysłana do akceptacji.', 'success');
        onSave?.(form);
        handleClose();
      })
      .catch((e) => {
        console.error('Nie udało się zapisać faktury:', e);
        showNotification(e?.response?.data?.error || 'Nie udało się zapisać faktury. Sprawdź konfigurację Google i spróbuj ponownie.', 'error');
      })
      .finally(() => setSaving(false));
  };

  // Sprawdzanie białej listy. Tryb `silent` (auto-check) nie pokazuje
  // toastów – użytkownik dostaje feedback przez inline napis pod polem.
  const validateNrbChecksumLocal = (nrb: string): boolean => {
    if (nrb.length !== 26 || !/^\d{26}$/.test(nrb)) return false;
    const reordered = nrb.slice(2) + '2521' + nrb.slice(0, 2);
    let remainder = 0;
    for (let i = 0; i < reordered.length; i++) { remainder = (remainder * 10 + parseInt(reordered[i], 10)) % 97; }
    return remainder === 1;
  };

  const runBankAccountCheck = async (silent: boolean = false) => {
    const cleanAccount = (form.bankAccount || '').replace(/[\s-]/g, '');
    if (!cleanAccount) {
      if (!silent) showNotification('Wpisz numer rachunku bankowego.', 'warning');
      return;
    }
    let nip = (form.nip || '').replace(/[\s-]/g, '') || selectedContractorNip;
    if (!nip && form.contractorId) {
      try {
        const contractor = await contractorService.getContractorById(form.contractorId);
        if (contractor?.nip) {
          nip = contractor.nip;
          setSelectedContractorNip(nip);
          setForm((p) => ({ ...p, nip }));
        }
      } catch {}
    }
    const cleanNip = (nip || '').replace(/[\s-]/g, '');
    if (!cleanNip) {
      if (!silent) showNotification('Brak NIP kontrahenta — wpisz NIP lub wybierz kontrahenta z bazy.', 'warning');
      return;
    }
    setBankAccountCheckStatus('checking');
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get('/white-list/check', {
        params: { nip: cleanNip, bankAccount: cleanAccount, date: today },
      });
      const data = response.data;
      const checkedAt = new Date().toISOString();
      const assigned = data?.result?.accountAssigned;
      if (data.success && assigned === 'TAK') {
        setBankAccountCheckStatus('valid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek figuruje na białej liście VAT.', 'success');
      } else if (data.success && assigned === 'VIRTUAL_MATCH') {
        setBankAccountCheckStatus('valid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) {
          const base = data.result?.matchingBaseAccount || '';
          showNotification(
            `Rachunek wirtualny — bazowy rachunek ${base ? base.replace(/(.{2})(.{4})(.{4})(.{4})(.{4})(.{4})(.{4})/, '$1 $2 $3 $4 $5 $6 $7') : ''} figuruje na białej liście VAT.`,
            'success'
          );
        }
      } else if (data.success && assigned === 'VIRTUAL_NO_MATCH') {
        setBankAccountCheckStatus('invalid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) {
          const accounts = (data.result?.registeredAccounts || []) as string[];
          const company = data.result?.companyName || '';
          const msg = accounts.length > 0
            ? `Rachunek nie pasuje do białej listy. Zarejestrowane rachunki${company ? ` (${company})` : ''}: ${accounts.slice(0, 3).join(', ')}${accounts.length > 3 ? '...' : ''}`
            : `Rachunek NIE figuruje na białej liście VAT.`;
          showNotification(msg, 'warning');
        }
      } else if (data.success && assigned === 'PROBABLE_MATCH') {
        setBankAccountCheckStatus('invalid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) {
          const base = data.result?.matchingBaseAccount || '';
          showNotification(
            `Rachunek wirtualny — pasuje bank/oddział (8 cyfr), ale nie pełny numer. Weryfikacja wymaga potwierdzenia u kontrahenta${base ? ` (bazowy: ${base.slice(0,2)} ${base.slice(2,6)} ${base.slice(6,10)}...)` : ''}.`,
            'warning'
          );
        }
      } else if (data.success && assigned === 'FOREIGN_ACCOUNT') {
        setBankAccountCheckStatus('valid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek zagraniczny — biała lista VAT nie obejmuje kont spoza Polski.', 'info');
      } else if (data.success && assigned === 'NIE') {
        setBankAccountCheckStatus('invalid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek NIE figuruje na białej liście VAT!', 'warning');
      } else {
        setBankAccountCheckStatus('invalid');
        setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification(data.message || 'Nie można zweryfikować rachunku.', 'warning');
      }
    } catch (error: any) {
      console.error('Błąd weryfikacji rachunku na białej liście:', error);
      setBankAccountCheckStatus('error');
      setBankAccountCheckedAt(null);
      if (!silent) {
        const msg = error?.response?.data?.message || error?.response?.data?.data?.message || 'Nie udało się zweryfikować rachunku na białej liście VAT. Spróbuj ponownie.';
        showNotification(msg, 'error');
      }
    }
  };

  const checkBankAccount = () => runBankAccountCheck(false);

  // Auto-sprawdzanie białej listy po wczytaniu / edycji – debounced 500 ms.
  React.useEffect(() => {
    if (!open) return;
    if (bankAccountCheckStatus !== 'idle') return;
    const cleanAccount = (form.bankAccount || '').replace(/[\s-]/g, '');
    if (cleanAccount.length !== 26 || !/^\d{26}$/.test(cleanAccount)) return;
    if (!validateNrbChecksumLocal(cleanAccount)) return;
    const cleanNip = ((form.nip || '').replace(/[\s-]/g, '') || selectedContractorNip || '').replace(/[\s-]/g, '');
    if (cleanNip.length !== 10 || !/^\d{10}$/.test(cleanNip)) return;
    const t = setTimeout(() => { void runBankAccountCheck(true); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.bankAccount, form.nip, selectedContractorNip, bankAccountCheckStatus]);

  const startPhoneScan = async () => {
    if (scanWaiting) return;
    setScanWaiting(true);
    try {
      const r = await api.post('/scan-requests', { type: 'transfer-invoice' });
      const sid = r?.data?.data?.sessionId || null;
      if (!sid) throw new Error('Nie udało się utworzyć żądania skanu.');
      setScanSessionId(sid);
      const s = getSocket();
      const handler = async (payload: any) => {
        try {
          if (!payload || payload.sessionId !== sid || !payload.url) return;
          // pobierz PDF jako blob i podłóż do formularza (użyj origin backendu)
          const baseURL = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
          const origin = baseURL.replace(/\/?api\/?$/, '');
          const absUrl = payload.url.startsWith('http') ? payload.url : `${origin}${payload.url}`;
          const resp = await fetch(absUrl);
          const blob = await resp.blob();
          const f = new File([blob], `scan-${Date.now()}.pdf`, { type: 'application/pdf' });
          onDropFile(f);
        } catch {}
        // posprzątaj
        try { s.off('scan:completed', handler); } catch {}
        scanHandlerRef.current = null;
        setScanWaiting(false);
        setScanSessionId(null);
      };
      scanHandlerRef.current = handler;
      s.on('scan:completed', handler);
    } catch (e) {
      setScanWaiting(false);
      setScanSessionId(null);
      showNotification('Nie udało się wysłać prośby o skan. Upewnij się, że aplikacja mobilna jest zainstalowana i zalogowana.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen
      PaperProps={{ sx: { bgcolor: '#FAFAFA' } }}>
      {/* Górny pasek */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #EDEDED', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <Tooltip title={user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'Użytkownik'}>
            <Avatar src={user?.avatar_url || undefined} sx={{ width: 28, height: 28, flexShrink: 0 }}>
              {getInitials(user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'U')}
            </Avatar>
          </Tooltip>
          <KeyboardArrowRight sx={{ color: 'text.secondary', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#020242', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Dodaj nową fakturę (przelewową, gotówkową lub proforma)
          </Typography>
        </Box>
      </Box>

      {/* Główna zawartość – podział 60/40 */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', p: 2, gap: 2 }}>
        {/* Lewa kolumna – podgląd PDF (50%) */}
        <Box sx={{ flex: '0 0 50%', minWidth: 0 }}>
          <Box
            sx={{
              border: '2px dashed #d4d4d9',
              borderRadius: '4px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: fileUrl ? '#fff' : 'transparent',
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onDropFile(f);
            }}
          >
            {!fileUrl ? (
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <PictureAsPdf sx={{ fontSize: 56, color: 'text.disabled' }} />
                <Typography sx={{ mt: 1, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>Przeciągnij i upuść plik PDF tutaj</Typography>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#B9B9B9' }}>lub</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" component="label" size="small"
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', borderColor: '#d4d4d9', color: '#2D2D32' }}>
                    Wybierz plik PDF
                    <input type="file" hidden accept="application/pdf" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onDropFile(f);
                    }} />
                  </Button>
                  <Button variant="outlined" onClick={startPhoneScan} startIcon={<PhoneIphone />} disabled={scanWaiting} size="small"
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', borderColor: '#d4d4d9', color: '#2D2D32' }}>
                    {scanWaiting ? 'Oczekiwanie…' : 'Skan z telefonu'}
                  </Button>
                </Box>
                {scanWaiting && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>Sprawdź powiadomienie w aplikacji mobilnej</Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <iframe title="invoice-preview" src={fileUrl} style={{ width: '100%', height: '100%', border: 0 }} />
            )}
          </Box>
        </Box>

          {/* Prawa kolumna – formularz z zakładkami (50%) */}
          <Box sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: '4px', border: '1px solid #EDEDED', overflow: 'hidden' }}>
            {/* Zakładki */}
            <Box sx={{ borderBottom: '1px solid #EDEDED', px: 2 }}>
              <Tabs
                value={formTab}
                onChange={(_, v) => setFormTab(v)}
                variant="standard"
                sx={{
                  minHeight: 36,
                  '& .MuiTabs-indicator': { height: 2, bgcolor: '#000000' },
                  '& .MuiTabs-flexContainer': { gap: 0 }
                }}
              >
                <Tab
                  disableRipple
                  label="Informacje"
                  sx={{
                    textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400,
                    color: '#020242', minHeight: 36, px: 2, py: 0,
                    '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                  }}
                />
                <Tab
                  disableRipple
                  label="Wartości i pozycje"
                  sx={{
                    textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400,
                    color: '#020242', minHeight: 36, px: 2, py: 0,
                    '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                  }}
                />
              </Tabs>
            </Box>

            {/* Karta: Informacje */}
            <Box sx={{ flex: 1, overflowY: 'auto', pt: 2, px: 2, display: formTab === 0 ? 'block' : 'none' }}>
              <Grid container rowSpacing={1.5} columnSpacing={2}>
                <Grid item xs={6}>
                  <FieldLabel text="Data dokumentu" required />
                  <TextField
                    fullWidth type="date" value={form.invoiceDate}
                    onChange={(e) => setForm((p) => ({ ...p, invoiceDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                    error={showErrors && !form.invoiceDate}
                    helperText={showErrors && !form.invoiceDate ? 'Wymagane' : ''}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Data otrzymania" required />
                  <TextField
                    fullWidth type="date" value={form.receivedDate}
                    onChange={(e) => setForm((p) => ({ ...p, receivedDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                    error={showErrors && !form.receivedDate}
                    helperText={showErrors && !form.receivedDate ? 'Wymagane' : ''}
                  />
                </Grid>
                <Grid item xs={8.4}>
                  <FieldLabel text="Projekt" required />
                  <Autocomplete
                    size="small"
                    options={projects}
                    loading={loadingProjects}
                    getOptionLabel={(p) => `${p.project_id} ${p.project_name}`}
                    value={projects.find((p) => p.project_id === form.projectId) || null}
                    onChange={(_, val) => setForm((prev) => ({ ...prev, projectId: val ? String(val.project_id) : '' }))}
                    isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
                    ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '12px', py: 0.5, minHeight: 28 } } }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Wybierz projekt" sx={textFieldSx}
                        error={showErrors && !form.projectId}
                        helperText={showErrors && !form.projectId ? 'Wymagane' : ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3.6}>
                  <FieldLabel text="Magazyn" />
                  <TextField fullWidth size="small" value={form.warehouse || ''} placeholder="000"
                    onChange={(e) => { const v = e.target.value.slice(0, 3).toUpperCase(); setForm((p) => ({ ...p, warehouse: v })); }}
                    inputProps={{ maxLength: 3 }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={7.8}>
                  <FieldLabel text="Nr KSeF" />
                  <TextField fullWidth value={form.ksefNumber || ''}
                    onChange={(e) => setForm((p) => ({ ...p, ksefNumber: e.target.value }))}
                    size="small" sx={textFieldSx} placeholder="Nr KSeF"
                  />
                </Grid>
                <Grid item xs={4.2}>
                  <FieldLabel text="Nr Faktury" />
                  <TextField fullWidth value={form.invoiceNumber}
                    onChange={(e) => setForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                    size="small" sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={7.8}>
                  <FieldLabel text="Kontrahent" required />
                  <Autocomplete
                    size="small" freeSolo options={contractorOptions} loading={loadingContractors}
                    inputValue={contractorSearchQuery}
                    onInputChange={(_, newValue, reason) => {
                      setContractorSearchQuery(newValue);
                      if (reason === 'input') {
                        searchContractors(newValue);
                      }
                    }}
                    value={null}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'string') {
                        setForm((prev) => ({ ...prev, contractorId: undefined, contractorName: newValue.trim(), nip: '', contractorAddress: '', contractorPostalCity: '' }));
                        setContractorSearchQuery(newValue.trim());
                        setSelectedContractorNip('');
                      } else if (newValue && typeof newValue === 'object' && 'id' in newValue) {
                        const c = newValue as Contractor;
                        const nipVal = c.nip || '';
                        setForm((prev) => ({
                          ...prev,
                          contractorId: c.id,
                          contractorName: c.name,
                          nip: nipVal,
                          contractorAddress: c.address || '',
                          contractorPostalCity: c.location || '',
                        }));
                        setContractorSearchQuery(c.name);
                        setSelectedContractorNip(nipVal);
                      } else {
                        setForm((prev) => ({ ...prev, contractorId: undefined, contractorName: '', nip: '', contractorAddress: '', contractorPostalCity: '' }));
                        setContractorSearchQuery('');
                        setSelectedContractorNip('');
                      }
                      setBankAccountCheckStatus('idle');
                      setContractorOptions([]);
                    }}
                    onBlur={() => {
                      const trimmed = contractorSearchQuery.trim();
                      if (trimmed) {
                        setForm((prev) => ({ ...prev, contractorName: trimmed }));
                      } else if (!form.contractorId) {
                        setForm((prev) => ({ ...prev, contractorName: '', contractorId: undefined }));
                      }
                    }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option && 'name' in option ? option.name : '')}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof option === 'string' && typeof value === 'string') return (option as string).trim() === (value as string).trim();
                      if (typeof option === 'object' && typeof value === 'object' && option !== null && value !== null && 'id' in option && 'id' in value) return String((option as Contractor).id) === String((value as Contractor).id);
                      if (typeof option === 'string' && typeof value === 'object' && value !== null && 'name' in value) return (option as string).trim() === (value as Contractor).name.trim();
                      if (typeof option === 'object' && typeof value === 'string' && option !== null && 'name' in option) return (option as Contractor).name.trim() === (value as string).trim();
                      return false;
                    }}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Wpisz nazwę kontrahenta..." sx={textFieldSx}
                        error={showErrors && !form.contractorName}
                        helperText={showErrors && !form.contractorName ? 'Wymagane' : ''}
                        InputProps={{ ...params.InputProps, endAdornment: (<>{loadingContractors ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                      />
                    )}
                    renderOption={(props, option) => typeof option === 'string' ? (
                      <Box component="li" {...props} key={option}><Typography variant="body2">{option}</Typography></Box>
                    ) : (
                      <Box component="li" {...props} key={option.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.location ? `${option.location}` : ''}{option.location && option.nip ? ', ' : ''}{option.nip ? `NIP: ${option.nip}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText={contractorSearchQuery.length < 2 ? 'Wpisz co najmniej 2 znaki' : 'Brak wyników. Możesz wprowadzić kontrahenta spoza bazy.'}
                  />
                </Grid>
                <Grid item xs={4.2}>
                  <FieldLabel text="NIP" />
                  <TextField fullWidth size="small" value={form.nip || ''} placeholder="NIP kontrahenta"
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((p) => ({ ...p, nip: val }));
                      searchContractorsByNip(val);
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={7.8}>
                  <FieldLabel text="Adres (ulica i numer)" />
                  <TextField fullWidth size="small" value={form.contractorAddress || ''} placeholder="np. ul. Gałczyńskiego 4"
                    onChange={(e) => setForm((p) => ({ ...p, contractorAddress: e.target.value }))}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={4.2}>
                  <FieldLabel text="Kod pocztowy i miejscowość" />
                  <TextField fullWidth size="small" value={form.contractorPostalCity || ''} placeholder="np. 00-362 Warszawa"
                    onChange={(e) => setForm((p) => ({ ...p, contractorPostalCity: e.target.value }))}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Nr rachunku bankowego" />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Stała szerokość pola dla 26 cyfr + 6 spacji
                        (32 znaki ≈ 360 px). Reszta wiersza dla statusu. */}
                    <TextField
                      size="small"
                      value={form.bankAccount || ''}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, bankAccount: e.target.value }));
                        setBankAccountCheckStatus('idle');
                        setBankAccountCheckedAt(null);
                      }}
                      placeholder="00 0000 0000 0000 0000 0000 0000"
                      sx={{ ...textFieldSx, width: 360, flexShrink: 0 }}
                    />
                    <Tooltip title={
                      bankAccountCheckStatus === 'valid' ? `Rachunek figuruje na białej liście VAT${bankAccountCheckedAt ? ' (' + new Date(bankAccountCheckedAt).toLocaleDateString('pl-PL') + ')' : ''}`
                        : bankAccountCheckStatus === 'invalid' ? `Rachunek NIE figuruje na białej liście VAT${bankAccountCheckedAt ? ' (' + new Date(bankAccountCheckedAt).toLocaleDateString('pl-PL') + ')' : ''}`
                        : bankAccountCheckStatus === 'checking' ? 'Sprawdzanie...'
                        : bankAccountCheckStatus === 'error' ? 'Błąd weryfikacji – kliknij ponownie'
                        : !form.nip && !form.contractorId && !selectedContractorNip ? 'Wybierz kontrahenta z bazy (z NIP) aby sprawdzić'
                        : !(form.bankAccount || '').replace(/[\s-]/g, '') ? 'Wpisz numer rachunku'
                        : 'Sprawdź na białej liście VAT (API MF)'
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={checkBankAccount}
                          disabled={!(form.bankAccount || '').replace(/[\s-]/g, '') || (!form.nip && !selectedContractorNip && !form.contractorId) || bankAccountCheckStatus === 'checking'}
                          sx={{
                            border: '1px solid',
                            borderColor: bankAccountCheckStatus === 'valid' ? '#4caf50' : bankAccountCheckStatus === 'invalid' ? '#f44336' : '#d4d4d9',
                            borderRadius: '4px', width: 32, height: 32,
                            bgcolor: bankAccountCheckStatus === 'valid' ? '#e8f5e9' : bankAccountCheckStatus === 'invalid' ? '#ffebee' : 'transparent',
                            '&:hover': { borderColor: bankAccountCheckStatus === 'valid' ? '#388e3c' : bankAccountCheckStatus === 'invalid' ? '#d32f2f' : '#595966',
                              bgcolor: bankAccountCheckStatus === 'valid' ? '#c8e6c9' : bankAccountCheckStatus === 'invalid' ? '#ffcdd2' : '#f5f5f5' },
                          }}
                        >
                          {bankAccountCheckStatus === 'checking' ? (
                            <CircularProgress size={16} />
                          ) : bankAccountCheckStatus === 'valid' ? (
                            <CheckCircle sx={{ fontSize: 20, color: '#4caf50' }} />
                          ) : bankAccountCheckStatus === 'invalid' ? (
                            <CancelIcon sx={{ fontSize: 20, color: '#f44336' }} />
                          ) : (
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: bankAccountCheckStatus === 'error' ? '#f44336' : '#595966', lineHeight: 1 }}>WL</Typography>
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        color:
                          bankAccountCheckStatus === 'valid' ? '#2e7d32'
                            : bankAccountCheckStatus === 'invalid' ? '#d32f2f'
                            : bankAccountCheckStatus === 'error' ? '#d32f2f'
                            : '#9e9e9e',
                      }}
                    >
                      {bankAccountCheckStatus === 'valid' ? 'Na białej liście VAT'
                        : bankAccountCheckStatus === 'invalid' ? 'Brak na białej liście VAT'
                        : bankAccountCheckStatus === 'checking' ? 'Sprawdzanie…'
                        : bankAccountCheckStatus === 'error' ? 'Błąd weryfikacji'
                        : ''}
                      {(bankAccountCheckStatus === 'valid' || bankAccountCheckStatus === 'invalid') && bankAccountCheckedAt
                        ? ` (${new Date(bankAccountCheckedAt).toLocaleDateString('pl-PL')})`
                        : ''}
                    </Typography>
                  </Box>
                </Grid>
                {/* Wprowadził – domyślnie aktualnie zalogowany user; można zmienić.
                    Backend zapisuje `created_by_user_id` (kolumna „W" w tabeli). */}
                <Grid item xs={6}>
                  <FieldLabel text="Wprowadził" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select
                      value={form.enteredByUserId || ''}
                      displayEmpty
                      onChange={(e) => setForm((p) => ({ ...p, enteredByUserId: String(e.target.value) }))}
                      renderValue={(value) => {
                        const u = enteredByUsers.find((x) => x.user_id === value);
                        const fallbackName = (user as any)?.display_name;
                        const fallbackAvatar = (user as any)?.avatar_url;
                        if (!value) {
                          return <Box component="span" sx={{ color: '#9e9e9e' }}>Wybierz użytkownika</Box>;
                        }
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={u?.avatar_url || fallbackAvatar || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                              {(u?.display_name || fallbackName || '?').substring(0, 1)}
                            </Avatar>
                            <Box component="span">{u?.display_name || fallbackName || value}</Box>
                          </Box>
                        );
                      }}
                    >
                      {enteredByUsers.map((u) => (
                        <MenuItem key={u.user_id} value={u.user_id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                              {(u.display_name || u.email || '?').substring(0, 1)}
                            </Avatar>
                            <Box component="span">{u.display_name || u.email}</Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={6}>
                  <FieldLabel text="Termin płatności" required />
                  <TextField fullWidth type="date" value={form.dueDate}
                    onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                    error={showErrors && !form.dueDate}
                    helperText={showErrors && !form.dueDate ? 'Wymagane' : ''}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Status" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.priority}
                      onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value as 'zwykły' | 'pilny' }))}>
                      <MenuItem value={'zwykły'}>zwykły</MenuItem>
                      <MenuItem value={'pilny'}>pilny</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Rodzaj" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.documentKind}
                      onChange={(e) => setForm((p) => ({ ...p, documentKind: e.target.value as 'vat' | 'advance' | 'proforma' | 'correction' }))}
                      displayEmpty renderValue={(v) => (v ? ({ vat: 'Faktura VAT', advance: 'Zaliczka', proforma: 'Proforma', correction: 'Korekta' } as Record<string, string>)[v] || String(v) : 'Rodzaj')}>
                      <MenuItem value={'vat'}>Faktura VAT</MenuItem>
                      <MenuItem value={'advance'}>Zaliczka</MenuItem>
                      <MenuItem value={'proforma'}>Proforma</MenuItem>
                      <MenuItem value={'correction'}>Korekta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Sposób płatności" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.paymentMethod}
                      onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value as 'Przelew' | 'Proforma' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)' }))}
                      displayEmpty renderValue={(v) => (v ? `${v}` : 'Sposób płatności')}>
                      <MenuItem value={'Przelew'}>Przelew</MenuItem>
                      <MenuItem value={'Proforma'}>Proforma</MenuItem>
                      <MenuItem value={'Gotówka'}>Gotówka</MenuItem>
                      <MenuItem value={'Karta'}>Karta</MenuItem>
                      <MenuItem value={'Zaliczka (przelew)'}>Zaliczka (przelew)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0, mt: 1 }}
                    control={
                      <Checkbox checked={form.legalAcceptance}
                        onChange={(e) => setForm((p) => ({ ...p, legalAcceptance: e.target.checked }))}
                        sx={{ mt: 0, p: 0.5 }} size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', textAlign: 'justify', lineHeight: 1.4, ml: 0.5 }}>
                        Potwierdzam, że zgłaszana faktura przelewowa jest bezpośrednio związana z realizacją wybranego projektu i spełnia wymogi kwalifikowalności wydatków określone w OWU. Przyjmuję odpowiedzialność za rzetelność tego oświadczenia zgodnie z § 2.3.1 OWU.
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Karta: Wartości i pozycje */}
            <Box sx={{ flex: 1, display: formTab === 1 ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Przełącznik z pozycjami / bez pozycji + tryb obliczeń */}
              <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d4d4d9' }}>
                  <Button size="small" onClick={() => setWithLineItems(false)}
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '12px', fontWeight: 500, borderRadius: 0, px: 1.5, py: 0.4,
                      backgroundColor: !withLineItems ? '#4040DB' : '#fff', color: !withLineItems ? '#fff' : '#595966',
                      '&:hover': { backgroundColor: !withLineItems ? '#1F1FB0' : '#f5f5f5' }, minWidth: 0 }}>
                    Bez pozycji
                  </Button>
                  <Button size="small" onClick={() => setWithLineItems(true)}
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '12px', fontWeight: 500, borderRadius: 0, px: 1.5, py: 0.4, borderLeft: '1px solid #d4d4d9',
                      backgroundColor: withLineItems ? '#4040DB' : '#fff', color: withLineItems ? '#fff' : '#595966',
                      '&:hover': { backgroundColor: withLineItems ? '#1F1FB0' : '#f5f5f5' }, minWidth: 0 }}>
                    Z pozycjami
                  </Button>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150, ml: 'auto', ...selectSx, '& .MuiOutlinedInput-root': { height: 30 }, '& .MuiSelect-select': { fontSize: '12px', py: '4px' } }}>
                  <Select value={form.calcMode === 'none' || !form.calcMode ? 'net' : form.calcMode}
                    onChange={(e) => {
                      const newValue = e.target.value as 'net' | 'gross';
                      if (!withLineItems && form.vatRate === 'różne') return;
                      // Sama zmiana trybu nie modyfikuje wartości – decyduje
                      // tylko, które pole jest read-only.
                      setForm((p) => ({ ...p, calcMode: newValue }));
                    }}
                    disabled={!withLineItems && form.vatRate === 'różne'}>
                    <MenuItem value="net">Licz od netto</MenuItem>
                    <MenuItem value="gross">Licz od brutto</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Pozycje faktury – scrollowalny obszar (tylko gdy z pozycjami) */}
              {withLineItems && (
              <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#020242' }}>
                    Pozycje faktury
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5, fontFamily: 'Roboto, sans-serif', fontSize: '12px', borderColor: '#EDEDED' } }}>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 500, color: '#595966', fontSize: '11px', borderBottom: '1px solid #d4d4d9', whiteSpace: 'nowrap' } }}>
                        <TableCell sx={{ width: 28 }}>L.p</TableCell>
                        <TableCell>Nazwa</TableCell>
                        <TableCell sx={{ width: 50 }} align="right">Ilość</TableCell>
                        <TableCell sx={{ width: 65 }} align="center">Jm</TableCell>
                        <TableCell sx={{ width: 80 }} align="right">{form.calcMode === 'gross' ? 'Cena brutto' : 'Cena netto'}</TableCell>
                        <TableCell sx={{ width: 70 }} align="center">VAT(%)</TableCell>
                        <TableCell sx={{ width: 80 }} align="right">{form.calcMode === 'gross' ? 'W. brutto' : 'W. netto'}</TableCell>
                        <TableCell sx={{ width: 28 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((item, idx) => (
                        <TableRow key={item.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ color: '#595966' }}>{idx + 1}</TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" value={item.name} placeholder="Nazwa"
                              onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                              sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 8px', fontSize: '12px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" value={item.quantity} placeholder="1"
                              onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value === '' ? '' : Number(e.target.value))}
                              inputProps={{ step: '1', min: 0 }}
                              sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 6px', fontSize: '12px', textAlign: 'right' }, width: 50 }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ width: 65, ...selectSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiSelect-select': { padding: '4px 8px', fontSize: '12px' } }}>
                              <Select value={item.unit} onChange={(e) => updateLineItem(item.id, 'unit', e.target.value)}>
                                {ensureUnitOptions(item.unit).map((u) => (
                                  <MenuItem key={u} value={u}>{u}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            {form.calcMode === 'gross' ? (
                              <TextField size="small" type="number" value={fmtPrice(item.unitGrossPrice, `line-${item.id}-ugp`)} placeholder="0.00"
                                onChange={(e) => updateLineItem(item.id, 'unitGrossPrice', e.target.value === '' ? '' : Number(e.target.value))}
                                onFocus={() => setFocusedPriceField(`line-${item.id}-ugp`)}
                                onBlur={() => setFocusedPriceField(null)}
                                inputProps={{ step: '0.01', min: 0 }}
                                sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 6px', fontSize: '12px', textAlign: 'right' }, width: 80 }}
                              />
                            ) : (
                              <TextField size="small" type="number" value={fmtPrice(item.unitNetPrice, `line-${item.id}-unp`)} placeholder="0.00"
                                onChange={(e) => updateLineItem(item.id, 'unitNetPrice', e.target.value === '' ? '' : Number(e.target.value))}
                                onFocus={() => setFocusedPriceField(`line-${item.id}-unp`)}
                                onBlur={() => setFocusedPriceField(null)}
                                inputProps={{ step: '0.01', min: 0 }}
                                sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 6px', fontSize: '12px', textAlign: 'right' }, width: 80 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <FormControl size="small" sx={{ width: 70, ...selectSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiSelect-select': { padding: '4px 8px', fontSize: '12px' } }}>
                              <Select value={item.vatRate} onChange={(e) => updateLineItem(item.id, 'vatRate', e.target.value)}>
                                <MenuItem value={23}>23%</MenuItem>
                                <MenuItem value={8}>8%</MenuItem>
                                <MenuItem value={0}>0%</MenuItem>
                                <MenuItem value={'zw'}>zw.</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#2D2D32' }}>
                              {form.calcMode === 'gross'
                                ? (typeof item.grossValue === 'number' ? item.grossValue.toFixed(2) : '—')
                                : (typeof item.netValue === 'number' ? item.netValue.toFixed(2) : '—')
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeLineItem(item.id)} disabled={lineItems.length <= 1}
                              sx={{ p: 0.25, color: '#B40003', '&.Mui-disabled': { color: '#d4d4d9' } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" size="small" onClick={addLineItem}
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', fontWeight: 500, borderRadius: '4px', backgroundColor: '#4040DB', color: '#fff', '&:hover': { backgroundColor: '#1F1FB0' }, '&:active': { backgroundColor: '#1A1A98' }, px: 1.5, py: 0.5 }}>
                    Dodaj pozycję
                  </Button>
                </Box>
              </Box>
              )}

              {/* Podsumowanie wartości – stałe na dole */}
              <Box sx={{ flexShrink: 0, borderTop: '1px solid #EDEDED', px: 2, pt: 2, pb: 2, bgcolor: '#fff' }}>
              <Grid container rowSpacing={1.5} columnSpacing={2}>
                <Grid item xs={6}>
                  <FieldLabel text="Wartość netto" required />
                  <TextField
                    fullWidth type="number" value={fmtPrice(form.netAmount, 'netAmount')}
                    onChange={(e) => {
                      if (withLineItems) return;
                      if (form.vatRate !== 'różne' && form.calcMode === 'gross') return;
                      const v = e.target.value === '' ? '' : Number(e.target.value);
                      setForm((p) => ({ ...p, ...recalcOnNetEdit(p, v) }));
                    }}
                    onFocus={() => setFocusedPriceField('netAmount')}
                    onBlur={() => setFocusedPriceField(null)}
                    size="small" inputProps={{ step: '0.01', min: 0 }} sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      readOnly: withLineItems || (form.vatRate !== 'różne' && form.calcMode === 'gross')
                    }}
                    error={showErrors && ((form.vatRate === 'różne' && !(typeof form.netAmount === 'number' && form.netAmount > 0)) || (form.vatRate !== 'różne' && form.calcMode === 'net' && !(typeof form.netAmount === 'number' && form.netAmount > 0)))}
                    helperText={showErrors && ((form.vatRate === 'różne' && !(typeof form.netAmount === 'number' && form.netAmount > 0)) || (form.vatRate !== 'różne' && form.calcMode === 'net' && !(typeof form.netAmount === 'number' && form.netAmount > 0))) ? 'Wymagana wartość > 0' : ''}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Stawka VAT" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.vatRate}
                      onChange={(e) => {
                        const v = e.target.value as any;
                        setForm((p) => ({ ...p, ...recalcOnVatRateEdit(p, v) }));
                      }}
                      disabled={withLineItems}>
                      <MenuItem value={23}>23%</MenuItem>
                      <MenuItem value={8}>8%</MenuItem>
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={'zw'}>zw.</MenuItem>
                      <MenuItem value={'różne'}>różne</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Wartość VAT" />
                  <TextField
                    fullWidth type="number"
                    value={(() => {
                      const net = typeof form.netAmount === 'number' ? form.netAmount : 0;
                      const gross = typeof form.grossAmount === 'number' ? form.grossAmount : 0;
                      const vat = round2(gross - net);
                      return vat > 0 ? vat.toFixed(2) : '';
                    })()}
                    size="small" inputProps={{ step: '0.01', min: 0 }} sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Wartość brutto" />
                  <TextField
                    fullWidth type="number" value={fmtPrice(form.grossAmount, 'grossAmount')}
                    onChange={(e) => {
                      if (withLineItems) return;
                      if (form.vatRate !== 'różne' && form.calcMode === 'net') return;
                      const v = e.target.value === '' ? '' : Number(e.target.value);
                      setForm((p) => ({ ...p, ...recalcOnGrossEdit(p, v) }));
                    }}
                    onFocus={() => setFocusedPriceField('grossAmount')}
                    onBlur={() => setFocusedPriceField(null)}
                    size="small" inputProps={{ step: '0.01', min: 0 }} sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      readOnly: withLineItems || (form.vatRate !== 'różne' && form.calcMode === 'net')
                    }}
                  />
                </Grid>
              </Grid>
              </Box>
            </Box>

          </Box>
        </Box>

      {/* Dolny pasek z przyciskami */}
      <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #EDEDED', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, gap: 1 }}>
        <Button onClick={() => { if (window.confirm('Czy na pewno chcesz wyczyścić formularz?')) { resetState(); setLineItems([emptyLineItem()]); } }} startIcon={<DeleteOutline />} color="error" size="small"
          sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', whiteSpace: 'nowrap', mr: 'auto' }}>
          Usuń
        </Button>
        <Button onClick={handleClose} disabled={saving}
          sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966', whiteSpace: 'nowrap' }}>
          Anuluj
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}
          sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, backgroundColor: '#020242', '&:hover': { backgroundColor: '#030360' }, whiteSpace: 'nowrap' }}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddTransferInvoiceDialog;


